import debounce from 'lodash/debounce';
import {
	AvailabilityListDocument,
	type AvailabilityListQuery,
	useAvailabilityListLazyQuery,
	type User,
} from '@/generated/graphql';
import { dateToMysqlString } from '@/composables/settings/useFormatter/formatterUtils';
import { resetTime } from '@/utils/formatting';
import type { UserAvailability } from '@/features/ResourcePlanning';
import { max, min } from 'date-fns';
import { ref } from 'vue';

let hasLoadMissingAvailabilityEvent = false;
let usersMissingAvailability: string[] = [];
let usersMissingAvailabilityDates: Date[] = [];
const availability = ref<Record<User['id'], UserAvailability>>({});

const LOAD_MISSING_AVAILABILITY = 'load-missing-availability' as const;
export const MISSING_AVAILABILITY_LOADED =
	'missing-availability-loaded' as const;

export const addUserToLoadAvailabilityFor = (userId: string, date: Date) => {
	if (!usersMissingAvailability.includes(userId)) {
		usersMissingAvailability.push(userId);
	}

	usersMissingAvailabilityDates.push(date);

	debounce(() => {
		window.dispatchEvent(new Event(LOAD_MISSING_AVAILABILITY));
	}, 100)();
};

export const resetAvailabilityService = () => {
	usersMissingAvailability = [];
	usersMissingAvailabilityDates = [];
	availability.value = {};
};

export const useAvailabilityService = () => {
	const {
		onResult: onAvailabilityResult,
		load: loadAvailabilityList,
		loading,
	} = useAvailabilityListLazyQuery(
		{
			input: {
				startDate: dateToMysqlString(new Date()),
				endDate: dateToMysqlString(new Date()),
				userIds: [],
			},
		},
		{
			notifyOnNetworkStatusChange: true,
		}
	);

	const setAvailability = (
		availabilityList: AvailabilityListQuery['AvailabilityList']
	) => {
		availabilityList.forEach((item) => {
			const userAvailability = availability.value[item.userId] ?? {};

			userAvailability[dateToMysqlString(resetTime(item.date))] =
				item.availability;

			availability.value[item.userId] = userAvailability;
		});
	};

	const loadAvailability = (
		startDate: Date,
		endDate: Date,
		userIds: string[]
	) => {
		if (!loading.value) {
			loadAvailabilityList(AvailabilityListDocument, {
				input: {
					startDate: dateToMysqlString(startDate),
					endDate: dateToMysqlString(endDate),
					userIds,
				},
			});
		}
	};

	onAvailabilityResult(({ data }) => {
		if (!data) {
			return;
		}
		setAvailability(data.AvailabilityList);

		window.dispatchEvent(new Event(MISSING_AVAILABILITY_LOADED));
	});

	const loadMissingAvailability = () => {
		if (usersMissingAvailability.length > 0) {
			const startDate = min(usersMissingAvailabilityDates);
			const endDate = max(usersMissingAvailabilityDates);

			loadAvailability(startDate, endDate, usersMissingAvailability);
			usersMissingAvailability = [];
			usersMissingAvailabilityDates = [];
		}
	};

	const getUserAvailability = (userId: User['id']) =>
		availability.value[userId] ?? {};

	if (!hasLoadMissingAvailabilityEvent) {
		window.addEventListener(LOAD_MISSING_AVAILABILITY, () =>
			loadMissingAvailability()
		);
		hasLoadMissingAvailabilityEvent = true;
	}

	return {
		loadMissingAvailability,
		loadAvailability,
		getUserAvailability,
	};
};
